import React, { useEffect, useState } from 'react';
import type { CompanyAiOnlineQuoteFile } from '@/services/companyAiService';
import { Button } from '@/ui/components';

const MAX_PREVIEW_BYTES = 30 * 1024 * 1024;
const types: Record<string, string> = {
  pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp', txt: 'text/plain', csv: 'text/plain',
};
export function quotePreviewType(name: string) {
  return types[name.split('.').pop()?.toLowerCase() || ''] || null;
}

export default function OnlineQuoteFilePreview({ file }: { file: CompanyAiOnlineQuoteFile | null }) {
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [content, setContent] = useState<{ url: string; text: string; type: string } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(100);
  const name = localFile?.name || file?.fileName || '';
  const type = quotePreviewType(name);

  useEffect(() => { setLocalFile(null); }, [file]);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = '';
    setContent(null);
    setError('');
    setZoom(100);
    setLoading(false);
    if (!type || (!localFile && !file?.contentPath)) return;
    if ((localFile?.size || file?.fileSizeBytes || 0) > MAX_PREVIEW_BYTES) {
      setError('This file exceeds the 30 MB preview limit. Download it to view.');
      return;
    }
    setLoading(true);
    void (async () => {
      try {
        let blob: Blob;
        if (localFile) blob = localFile;
        else {
          const path = new URL(file!.contentPath!, window.location.origin);
          if (path.origin !== window.location.origin) throw new Error('Preview requires a secure PMapp file link.');
          const response = await fetch(path.href, { credentials: 'include', signal: controller.signal });
          if (!response.ok) throw new Error('File preview could not be loaded. Try again or download the file.');
          const length = Number(response.headers.get('content-length'));
          if (length > MAX_PREVIEW_BYTES) throw new Error('This file exceeds the 30 MB preview limit.');
          blob = await response.blob();
        }
        if (blob.size > MAX_PREVIEW_BYTES) throw new Error('This file exceeds the 30 MB preview limit.');
        const text = type === 'text/plain' ? await blob.text() : '';
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type }));
        setContent({ url: objectUrl, text, type });
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Preview unavailable.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file, localFile, type]);

  return (
    <section className="quote-preview" aria-label="File preview">
      <header className="quote-preview__header">
        <div><h3>File Preview</h3><p>{name ? `Selected: ${name}` : 'Select an attachment to preview'}</p></div>
        <div className="quote-preview__tools">
          <Button variant="secondary" disabled={!content || zoom <= 25} onClick={() => setZoom((n) => Math.max(25, n - 25))} aria-label="Zoom out">−</Button>
          <Button variant="secondary" disabled={!content} onClick={() => setZoom(100)} aria-label="Reset zoom">{zoom}%</Button>
          <Button variant="secondary" disabled={!content || zoom >= 300} onClick={() => setZoom((n) => Math.min(300, n + 25))} aria-label="Zoom in">+</Button>
        </div>
      </header>
      <div className="quote-preview__viewport" aria-busy={loading}>
        {loading ? <p role="status">Loading preview…</p> : error ? <p role="alert">{error}</p> : content ? (
          content.type === 'application/pdf' ? <iframe title={`PDF preview: ${name}`} src={`${content.url}#zoom=${zoom}`} />
            : content.type === 'text/plain' ? <pre style={{ fontSize: `${14 * zoom / 100}px` }}>{content.text}</pre>
              : <img src={content.url} alt={name} style={{ width: `${zoom}%` }} onError={() => setError('This image could not be displayed. Download it to view.')} />
        ) : <p>{name ? 'Preview is unavailable for this format or file link. Use Download File to open it.' : 'Select a file from this request or choose a local file.'}</p>}
      </div>
      <footer className="quote-preview__footer">
        <label className="quote-preview__upload">Choose local file
          <input type="file" aria-label="Choose local file for preview" accept={Object.keys(types).map((ext) => `.${ext}`).join(',')} onChange={(event) => {
            const next = event.target.files?.[0];
            if (next) {
              if (!quotePreviewType(next.name)) setError('Unsupported format. Choose PDF, JPG, PNG, GIF, WebP, BMP, TXT or CSV.');
              else setLocalFile(next);
            }
            event.target.value = '';
          }} />
        </label>
        {localFile ? <Button variant="secondary" onClick={() => setLocalFile(null)}>Back to attachment</Button> : null}
        <span>PDF, images, TXT, CSV · Up to 30 MB. Local preview only; files are not saved.</span>
      </footer>
    </section>
  );
}
